import { addMoney, mulMoney, percentMoney, scaled } from '../shop/line-money';
import type { ConsignmentInvoiceDetail } from '../../api/types';

/**
 * What the detail page recomputes in the browser while Admin is still typing a
 * quantity (docs/plans/consignment-invoice-parent §7), so a changed amount appears at once rather than after a round trip.
 *
 * Exact decimal arithmetic on money strings, never a JS number for a money value
 * (NF-04). It uses `shop/line-money.ts` and not `@miraki/money`, because that package
 * ships CommonJS only and the browser cannot load it (see the note in that file).
 *
 * The server recomputes all of it on save, and its answer replaces these. Only what
 * a quantity change can move is recomputed here: what has been paid is the server's
 * figure.
 */

type Order = ConsignmentInvoiceDetail['orders'][number];

const ZERO = '0.0000';

/** Quantity as typed. Anything that is not a whole number of 0 or more reads as the saved one. */
export function parseQuantity(text: string, fallback: number): number {
  if (!/^\d{1,6}$/.test(text.trim())) return fallback;
  return Number(text.trim());
}

/**
 * A quantity held to what the line can carry: never below `min`, never above `maxQuantity`.
 * The screens use a `min` of 1: a product on an invoice is on it, and taking it off is done by
 * leaving its order off, not by invoicing nothing of it. (If the most is below `min`, the most wins.)
 */
export function clampQuantity(q: number, maxQuantity: number, min = 0): number {
  const top = Math.max(0, maxQuantity);
  return Math.min(Math.max(min, Math.trunc(q)), top);
}

export function lineAmount(unitPrice: string, quantity: number): string {
  return mulMoney(unitPrice, quantity);
}

/**
 * What a quantity change can move, and nothing else.
 *
 * **There is deliberately no payable here.** What a retailer owes on a despatch is the courier
 * charge (SH-13) — the goods are consigned (SH-06) — and the courier charge has nothing to do with
 * the quantities Admin is typing. The one derivation of the payable is `computeInvoiceFigures` on
 * the server, and both screens read it from there, which is what stops Admin and the retailer
 * showing different numbers for one invoice. A `totalPayable` on this type is what let them.
 */
export interface DraftTotals {
  perOrder: Array<{ orderId: string; orderAmount: string; products: number }>;
  products: number;
  /** The wholesale value of what this invoice would send at the typed quantities. Not a debt. */
  consignedValue: string;
  /** @deprecated Retired advance model (D31/D33/D34). Computed, shown nowhere. */
  advanceRequired: string;
  courierCharge: string | null;
}

/** Totals for `orders` with `quantities` (by order line id) overriding the saved ones. */
export function draftTotals(
  orders: Order[], quantities: Record<string, number>, courierCharge: string | null,
): DraftTotals {
  let purchase = ZERO;
  let advance = ZERO;
  let products = 0;
  const perOrder = orders.map((o) => {
    let amount = ZERO;
    let n = 0;
    for (const l of o.lines) {
      const q = quantities[l.orderLineId] ?? l.quantity;
      amount = addMoney(amount, mulMoney(l.unitPrice, q));
      if (q > 0) n += 1;
    }
    purchase = addMoney(purchase, amount);
    advance = addMoney(advance, percentMoney(amount, o.advancePct));
    products += n;
    return { orderId: o.orderId, orderAmount: amount, products: n };
  });
  return {
    perOrder, products,
    consignedValue: purchase, advanceRequired: advance,
    courierCharge,
  };
}

/** True when any typed quantity differs from the one saved on the invoice. */
export function hasUnsavedQuantities(orders: Order[], quantities: Record<string, number>): boolean {
  return orders.some((o) => o.lines.some((l) => (quantities[l.orderLineId] ?? l.quantity) !== l.quantity));
}

export const isPositive = (amount: string): boolean => scaled(amount) > 0n;

/** Courier charge as typed; null when it is not a valid, non-negative amount. */
export function parseCharge(text: string): string | null {
  const t = text.trim();
  if (!/^\d{1,15}(\.\d{1,4})?$/.test(t)) return null;
  return t;
}

// ---------------------------------------------------------------- create page

/** The part of a candidate order the create page needs to price a selection. */
export interface PickableOrder {
  orderId: string;
  lines: Array<{ orderLineId: string; quantity: number; unitPrice: string }>;
}

/** The quantity a line is invoiced at: the one chosen, held to 1..available, else all that is available. */
export function chosenQuantity(
  line: { orderLineId: string; quantity: number }, quantities: Record<string, number>,
): number {
  return clampQuantity(quantities[line.orderLineId] ?? line.quantity, line.quantity, 1);
}

/** One order's amount at the quantities chosen. */
export function pickableTotal(o: PickableOrder, quantities: Record<string, number>): string {
  return o.lines.reduce((sum, l) => addMoney(sum, mulMoney(l.unitPrice, chosenQuantity(l, quantities))), ZERO);
}

export interface SelectionTotals {
  orders: number;
  products: number;
  amount: string;
}

/**
 * What the ticked orders come to, at the latest quantities. Each order counts once however the
 * selection or the list was built: the list is keyed by order id first, and only ids in `ticked`
 * are summed, so a repeated row or a repeated tick cannot add an order twice.
 */
export function selectionTotals(
  orders: PickableOrder[], ticked: ReadonlySet<string>, quantities: Record<string, number>,
): SelectionTotals {
  const byId = new Map(orders.map((o) => [o.orderId, o]));
  let amount = ZERO;
  let products = 0;
  let count = 0;
  for (const id of ticked) {
    const o = byId.get(id);
    if (!o) continue; // an order that has since left the list is not part of the selection
    amount = addMoney(amount, pickableTotal(o, quantities));
    products += o.lines.length;
    count += 1;
  }
  return { orders: count, products, amount };
}
